import React from "react";
import { Shield, Lock, Globe, Server, FileCheck, Activity, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function EnterpriseInfrastructure() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const complianceStandards = [{
    name: "SOC2 Type II"
  }, {
    name: "GDPR Ready"
  }, {
    name: "HIPAA Compliant"
  }, {
    name: "ISO 27001"
  }, {
    name: "CCPA"
  }];
  const features = [{
    title: "End-to-End Encryption",
    description: "All voice data is encrypted at rest and in transit using AES-256 and TLS 1.3 protocols, ensuring your conversations remain private.",
    icon: Lock
  }, {
    title: "Global Low-Latency Edge",
    description: "Our distributed edge network ensures <50ms latency for voice processing worldwide, delivering natural, real-time conversations.",
    icon: Globe
  }, {
    title: "99.99% Uptime SLA",
    description: "Built on redundant, multi-region cloud infrastructure designed to handle high-volume call concurrency without interruption.",
    icon: Activity
  }, {
    title: "Role-Based Access Control",
    description: "Granular permission settings allow you to manage team access and secure sensitive customer data with precision.",
    icon: FileCheck
  }];
  return <section id="enterprise-infrastructure" ref={ref} className="relative w-full py-32 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
        <Shield className="w-[800px] h-[800px] text-primary/5 opacity-[0.03] transform -rotate-12 translate-y-24" strokeWidth={0.5} />
      </div>

      <div className="container relative z-10 mx-auto px-8 max-w-7xl">
        {}
        <div className={`flex flex-col items-center text-center mb-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="mb-6 flex flex-wrap justify-center gap-3">
            {complianceStandards.map((std, i) => <Badge key={i} variant="secondary" className="px-4 py-1.5 text-sm font-medium bg-secondary/50 border border-border/50 backdrop-blur-sm rounded-[10px]" data-index={i}>
                <CircleCheck className="w-3.5 h-3.5 mr-1.5 text-primary" />
                {std.name}
              </Badge>)}
          </div>

          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 max-w-3xl">
            Secure, Scalable, and Built for Enterprise
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed font-light">
            MollyCall.ai is engineered with a security-first architecture. We protect your data with bank-grade encryption and ensure business continuity with a resilient global infrastructure.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {features.map((feature, i) => <div key={i} data-index={i} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <Card className="h-full border border-border/60 bg-card/50 backdrop-blur-sm hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 group rounded-[10px]">
                <CardHeader>
                  <div className="w-12 h-12 rounded-[10px] bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>

        {}
        <div className={`flex flex-col items-center justify-center text-center pt-8 border-t border-border/40 transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-muted-foreground mb-6">
            Need detailed compliance documentation or a security review?
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="px-8 rounded-[10px] hover:shadow-[0_10px_15px_-3px_rgba(80,70,229,0.3)] transition-shadow">
              <Link to="/ContactUsPage">Request Security Whitepaper</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="px-8 rounded-[10px]">
              <Link to="/ContactUsPage">Contact Compliance Team</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}